import React, { useCallback } from 'react'
import { string, array, object, bool } from 'prop-types'
import { connect } from 'react-redux'

import { useEnhancedList } from 'gatsby-theme-marketplace-base/src/context/EnhancedList'
import {
  getCategoriesFilterConfig,
  getDefaultFiltersConfig,
} from 'gatsby-theme-marketplace-base/src/utils/products/filtersConfig'
import {
  numberOfCampaignApps,
  isCampaignAppsCountOddNumber,
  CATEGORY_LIST_FILTERS_KEY,
} from 'gatsby-theme-marketplace-base/src/components/Category'
import {
  isFetchingUser,
  isUserInitialized,
  isPublishedAppsFetching,
  publishedApps as getPublishedApps,
} from 'gatsby-theme-marketplace-base/src/store/selectors'
import Sidebar from 'gatsby-theme-marketplace-base/src/components/Sidebar'
import ListHeader from 'gatsby-theme-marketplace-base/src/components/ProductList/Header'
import AppsSection from 'gatsby-theme-marketplace-base/src/components/Category/AppsSection'
import { handleCategorySelect } from 'gatsby-theme-marketplace-base/src/utils/products'
import { URLS } from 'gatsby-theme-marketplace-base/src/constants'
import { canUseWindow } from 'gatsby-theme-marketplace-base/src/utils/browser/window'

import styles from '../../layout/category-styles'
import { FEATURED_APP_CATEGORIES } from '../../constants/collections'
import { Collections } from './Collections'
import { HeaderBanner } from './HeaderBanner'

const Category = ({
  name,
  campaign,
  isFetching,
  publishedApps,
  allApps,
  filtersOpenOnInit,
  hasCategoryFilter = true,
}) => {
  const { items, sort, sortOptions, updateSort, filters, updateFilters } =
    useEnhancedList()

  const getCampaignApps = useCallback(() => {
    const campaignApps = campaign?.applications
    const apps = publishedApps || allApps

    if (!campaignApps || !apps) {
      return []
    }

    return campaignApps
      .filter((app) => !!apps[app.id])
      .map((app) => apps[app.id])
  }, [publishedApps, campaign, allApps])

  const campaignApps = getCampaignApps()

  const handleSelectCategory = useCallback(
    (_, value) => {
      handleCategorySelect({
        value,
        filters,
        sort,
        updateFilters,
      })
    },
    [filters, sort, updateFilters]
  )

  const filtersConfig = getDefaultFiltersConfig({
    filters,
    handleSelect: updateFilters,
    withOnlyProductRelatedApps: true,
  })

  if (hasCategoryFilter) {
    filtersConfig.unshift(
      getCategoriesFilterConfig(filters, handleSelectCategory)
    )
  }

  const sortConfig = {
    options: sortOptions,
    value: sort,
    onSortChange: updateSort,
  }
  const isAllApps = name === 'All' || name === URLS.APPS.LABEL

  const featuredCategory =
    canUseWindow &&
    FEATURED_APP_CATEGORIES.find(
      (category) => window.location.pathname === category.url
    )
  const isFeaturedCategory = !!featuredCategory

  return (
    <div css={styles.category}>
      <div className="content">
        <Sidebar
          updateFilters={updateFilters}
          filters={filters}
          className="sidebar"
        />

        <div className="body">
          {isAllApps && <Collections />}
          {!isAllApps && isFeaturedCategory && (
            <HeaderBanner category={featuredCategory} />
          )}

          {campaign && campaignApps && (
            <AppsSection
              isFeatured
              items={campaignApps.slice(0, numberOfCampaignApps)}
              isFetching={isFetching}
              css={styles.featuredCategories(isCampaignAppsCountOddNumber)}
            >
              <div className="name">
                <h1 className="title">{`Featured in ${name}`}</h1>
              </div>
            </AppsSection>
          )}

          <AppsSection items={items} isFetching={isFetching}>
            <ListHeader
              storageKey={CATEGORY_LIST_FILTERS_KEY}
              filters={filtersConfig}
              sort={sortConfig}
              title={name}
              isOpenOnInit={isFeaturedCategory || filtersOpenOnInit}
              isAllApps={isAllApps}
            />
          </AppsSection>
        </div>
      </div>
    </div>
  )
}

const mapStateToProps = (state) => ({
  publishedApps: getPublishedApps(state),
  isFetching:
    isPublishedAppsFetching(state) ||
    isFetchingUser(state) ||
    !isUserInitialized(state),
})

Category.propTypes = {
  name: string,
  campaign: object,
  campaignApps: array,
  isFetching: bool,
  publishedApps: object,
  allApps: array,
  filtersOpenOnInit: bool,
  hasCategoryFilter: bool,
}

export default connect(mapStateToProps, null)(Category)
